import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames';
import { useIntl } from 'react-intl';
import ColorSelect from '../../../components/ColorSelect';
import AttributePicker from '../../../components/common/AttributePicker';
import messages from './messages';
import { priceToString } from '../../../utils';
import { getCookie } from '../../../utils/common/common';
import styles from './VariantSelection.module.scss';

import {
  ATTRIBUTE_DESCRIPTION_COOKIE,
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
  VARIANT_ATTRIBUTE_OUT_OF_STOCK,
  VARIANT_ATTRIBUTE_NAME_SIZES,
  FEEL_ATTRIBUTE_NAME,
  DEPTH_ATTRIBUTE_NAME,
  KID_SHEETS_COLOR_KEY,
  SOFTSTRETCH_SHEETS_KEY,
  VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE,
  VARIANT_ATTRIBUTE_NAME_DISCOUNT_AMOUNT_TYPE,
  BUNDLE_VARIANT_ATTRIBUTE_NAME_SIZES,
  VARIANT_SELECTION_SIZE,
  VARIANT_ATTRIBUTE_NAME_PILLOW_HEIGHT,
  VARIANT_ATTRIBUTE_NAME_FOUNDATION_HEIGHT,
} from './constants';

import { isColorAttribute, getOptions } from './utils';
import AttributeModal from '../../../components/AttributeModal';
import Tooltip from '../../../components/common/Tooltip/Tooltip';
import SizeSelection from '../SizeSelection';
import { getAttributeKey } from '../../../utils/variants';
import bundlePdp from '../../../slices/bundlePdp';
import DropdownSelection from '../../../components/common/DropdownSelection';
import {
  PILLOW_HEIGHT_MAP,
  PILLOW_HEIGHT_NAME,
} from '../../../utils/common/constants';

const getAttributeTitleWithPrefix = ({ title, prefix }) => {
  if (!prefix) {
    return title;
  }

  const firstWordOfTitle = title.split(' ')[0] ?? '';
  if (firstWordOfTitle?.toLowerCase() === prefix.toLowerCase()) {
    return title;
  }
  return `${prefix} ${title}`;
};

const VARIANT_ATTRIBUTES_TO_FILTER = [
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
  VARIANT_ATTRIBUTE_OUT_OF_STOCK,
  VARIANT_ATTRIBUTE_NAME_DISCOUNT_AMOUNT_TYPE,
];

// TODO: Remove JSDoc comments when migrating to TypeScript
/**
 * @param {{
 * hidePillowSize?: boolean,
 * variants?: Array,
 * defaultSelectedSKU?: undefined | string,
 * onSelectVariant?: function,
 * isShowingPriceDetails?: boolean,
 * isShowingDiscountedPrices?: boolean,
 * productKey?: string | null,
 * productName?: string | null,
 * attributesExtraInfo?: Array,
 * attributesUpperTitle?: string | null,
 * discountSavings?: string | null,
 * attributeTitlePrefix?: string | null,
 * sortColors?: boolean,
 * isBundlePdp?: boolean,
 * isSecondSku?: boolean,
 * isBundleSizeMatching?: boolean,
 * isNonPrimaryBundleSelection?: boolean,
 * isShowingDiscountedPrices?: boolean,
 * }} props
 */
const VariantSelection = ({
  variants = [],
  defaultSelectedSKU = undefined,
  onSelectVariant,
  isShowingPriceDetails = true,
  isShowingDiscountedPrices = false,
  productKey = null,
  productName = null,
  attributesExtraInfo = [],
  attributesUpperTitle = undefined,
  discountSavings = undefined,
  attributeTitlePrefix = undefined,
  sortColors = true,
  isBundlePdp = false,
  isBundleSizeMatching = false,
  isNonPrimaryBundleSelection = false,
  hidePillowSize = false,
  size = VARIANT_SELECTION_SIZE.MEDIUM,
  isDropdown = false,
  isSecondSku = false,
}) => {
  const selectedVariant = variants.find(
    (variant) => variant.sku === defaultSelectedSKU,
  );
  const discountAmountType = getAttributeKey(
    selectedVariant?.attributes,
    VARIANT_ATTRIBUTE_NAME_DISCOUNT_AMOUNT_TYPE,
  );
  const getDiscountedPercentage = () => {
    const originalPrice =
      selectedVariant?.price?.centAmount +
      (selectedVariant?.discount?.centAmount ?? 0);

    return selectedVariant?.discount
      ? Math.floor(
          (selectedVariant?.discount?.centAmount / originalPrice) * 100,
        )
      : 0;
  };
  const sizeRef = useRef(null);
  const randomId = useMemo(() => Math.floor(Math.random() * 10000), []);
  const cookie = getCookie(ATTRIBUTE_DESCRIPTION_COOKIE);
  const isAttributeDescription =
    cookie?.value === 'show_description_test_pdp_selectors';
  const [colorDisclaimer, setColorDisclaimer] = useState(false);

  const [selectBoxAttributes, setSelectBoxAttributes] = useState([]);

  useEffect(() => {
    setSelectBoxAttributes([
      ...VARIANT_ATTRIBUTE_NAME_SIZES,
      VARIANT_ATTRIBUTE_NAME_PILLOW_HEIGHT,
      DEPTH_ATTRIBUTE_NAME,
      VARIANT_ATTRIBUTE_NAME_FOUNDATION_HEIGHT,
    ]);
  }, []);

  useEffect(() => {
    if (
      sizeRef?.current?.scrollTo &&
      sizeRef?.current.scrollWidth > sizeRef?.current.clientWidth
    ) {
      const containerClientWidth = sizeRef.current.clientWidth;
      const optionSelected = sizeRef.current.querySelector(
        '[data-selected=true]',
      );
      const optionSelectedWidth = optionSelected?.clientWidth;
      const optionOffsetLeft = optionSelected?.offsetLeft;

      if (optionSelected) {
        const scrollX =
          optionOffsetLeft + optionSelectedWidth / 2 - containerClientWidth / 2;

        sizeRef.current.scrollTo({
          top: 0,
          left: scrollX,
          behavior: 'smooth',
        });
      }
    }
  });

  const { formatMessage, formatNumber, locale } = useIntl();

  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [
    initiallySelectedAttributeValues,
    setInitiallySelectedAttributeValues,
  ] = useState();
  const [productVariantName, setProductVariantName] = useState();

  const { options, attributeNames } = getOptions(
    variants,
    locale,
    hidePillowSize,
  );

  const orderAttributeNames = (attrNames) =>
    [
      ...attrNames.filter(
        (attrName) => !isColorAttribute({ attributeName: attrName }),
      ),
      attrNames.find((attrName) =>
        isColorAttribute({ attributeName: attrName }),
      ),
    ].filter(Boolean);

  const findVariantByAttributeValues = useCallback(
    (attributeValues) =>
      variants?.find(({ attributes }) =>
        attributes
          .filter(({ name }) => !VARIANT_ATTRIBUTES_TO_FILTER.includes(name))
          .every((attr) =>
            Object.entries(attributeValues).find(
              ([selectedAttrName, selectedAttrValue]) =>
                selectedAttrName === attr.name &&
                selectedAttrValue?.key === attr.key,
            ),
          ),
      ),
    [variants],
  );

  const setNewSelectedAttributeValuesStateHandler = (
    newSelectedAttributeValuesState,
  ) => {
    const variantAttributeKey =
      selectedAttributeValues?.feel?.key[0].toUpperCase();
    setSelectedAttributeValues(newSelectedAttributeValuesState);
    setProductVariantName(
      `${productName?.replace(
        /\s/g,
        '',
      )}${variantAttributeKey}${selectedAttributeValues?.feel?.key.slice(1)}er`,
    );

    onSelectVariant({
      sku: findVariantByAttributeValues(newSelectedAttributeValuesState)?.sku,
    });
  };

  const getPriceText = useCallback(
    ({ variant, attributeName }) => {
      const attributesNames = variants[0].attributes
        .map((attribute) => attribute.name.toLowerCase())
        .filter(
          (name) =>
            !isColorAttribute({ attributeName: name }) &&
            name !== VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
        );

      if (!variant.price) {
        return { discountedPriceAsText: '', priceAsText: '' };
      }
      if (
        attributesNames.length === 1 ||
        attributeName.toLowerCase().indexOf('size') >= 0
      ) {
        const variantPrice = variant.price;
        const variantDiscount = variant.discount ? variant.discount : undefined;
        const discountedPrice = {
          ...variantPrice,
          centAmount: variantPrice.centAmount,
        };
        const price = {
          ...variantPrice,
          centAmount:
            variantPrice.centAmount + variantDiscount?.centAmount ?? 0,
        };

        return {
          discountedPriceAsText: priceToString({
            price: discountedPrice,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          }),
          discountedPrice,
          priceAsText:
            variantDiscount &&
            priceToString({
              price,
              maximumFractionDigits: 0,
              formatNumber,
              formatMessage,
              forceDigit: true,
            }),
          price,
        };
      }
      return { discountedPriceAsText: '', priceAsText: '' };
    },
    [variants, formatNumber, formatMessage],
  );

  useEffect(() => {
    // finds defaultSelectedVariant, Mastervariant and first variant
    const foundVariants = variants
      .filter((variant) => !variant.isOutOfStock)
      .reduce(
        (acc, curr, index) => ({
          ...acc,
          ...(curr.masterVariant === true ? { masterVariant: curr } : {}),
          ...(curr.sku === defaultSelectedSKU ? { defaultSelected: curr } : {}),
          ...(index === 0 ? { first: curr } : {}),
        }),
        {},
      );

    const variant =
      foundVariants.defaultSelected ||
      foundVariants.masterVariant ||
      foundVariants.first;

    const toAttributeValues = (currentVariant) =>
      currentVariant?.attributes?.reduce(
        (acc, { name, key }) => ({
          ...acc,
          [name]: { key },
        }),
        {},
      );

    const masterVariantAttributeValues = toAttributeValues(variant);

    // Variants added through "Additional Variants" can lack some attributes
    // (e.g. Harmony Anywhere has no pillow-height). Keep a value for those
    // attributes (previous selection, else master/first variant) so the
    // other variants remain selectable.
    const missingAttributeNames = [
      ...new Set(
        variants.flatMap(({ attributes }) =>
          (attributes ?? []).map(({ name }) => name),
        ),
      ),
    ].filter(
      (name) => masterVariantAttributeValues && !masterVariantAttributeValues[name],
    );

    if (missingAttributeNames.length) {
      const fallbackAttributeValues =
        toAttributeValues(foundVariants.masterVariant || foundVariants.first) ??
        {};
      setSelectedAttributeValues((previousAttributeValues) => ({
        ...Object.fromEntries(
          missingAttributeNames
            .map((name) => [
              name,
              previousAttributeValues?.[name] ?? fallbackAttributeValues[name],
            ])
            .filter(([, value]) => value),
        ),
        ...masterVariantAttributeValues,
      }));
    } else {
      setSelectedAttributeValues(masterVariantAttributeValues);
    }
    if (!initiallySelectedAttributeValues) {
      setInitiallySelectedAttributeValues(masterVariantAttributeValues);
    }
    const variantAttributeKey =
      masterVariantAttributeValues?.feel?.key[0].toUpperCase();
    const variantFeelKey = masterVariantAttributeValues?.feel?.key.slice(1);
    setProductVariantName(
      `${productName?.replace(
        /\s/g,
        '',
      )}${variantAttributeKey}${variantFeelKey}er`,
    );
  }, [
    defaultSelectedSKU,
    initiallySelectedAttributeValues,
    productName,
    variants,
  ]);

  const isKidsSheets = (color) =>
    !!KID_SHEETS_COLOR_KEY.find((kidsColor) => kidsColor === color);

  useEffect(() => {
    if (
      productKey === SOFTSTRETCH_SHEETS_KEY &&
      selectedAttributeValues?.color?.key &&
      isKidsSheets(selectedAttributeValues.color.key)
    ) {
      setColorDisclaimer(true);
    } else {
      setColorDisclaimer(false);
    }
  }, [productKey, selectedAttributeValues]);

  const isSingleDepthAttribute = useMemo(() => {
    if (selectedAttributeValues?.depth) {
      const depthAttributes = options[DEPTH_ATTRIBUTE_NAME].value;
      const depthVariants = depthAttributes
        .map(
          (attr) =>
            findVariantByAttributeValues({
              ...selectedAttributeValues,
              [DEPTH_ATTRIBUTE_NAME]: { key: attr.key },
            }) ?? null,
        )
        .filter((variant) => !!variant);
      if (depthVariants.length < 2) {
        return true;
      }
    }
    return false;
  }, [findVariantByAttributeValues, options, selectedAttributeValues]);

  const renderAttributeExtraInfo = ({ attributeName }) => {
    const data = attributesExtraInfo.find(
      (extraInfo) => extraInfo.attributeName === attributeName,
    );
    if (!data) {
      return null;
    }

    const title = data.text || (
      <div
        className={styles['info-icon']}
        onClick={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        role='button'
        aria-label={attributeName}
        tabIndex='0'
      />
    );

    if (data.isToolTip) {
      return (
        <Tooltip direction='right-edge' align='bottom' content={data.content}>
          {title}
        </Tooltip>
      );
    }
    return (
      <AttributeModal
        title={title}
        content={data.content}
        backButtonLabel={productName}
        type={data.modalType}
      />
    );
  };

  const renderDiscountElement = ({ attributeName, variant }) => {
    const price = getPriceText({
      variant,
      attributeName,
    })?.priceAsText;

    const discountedPrice = getPriceText({
      variant,
      attributeName,
    })?.discountedPriceAsText;

    const priceAndDiscountedPriceAreEqual = price === discountedPrice;

    return (
      <div className={styles['discount-element-container']}>
        <div
          className={classnames({
            [styles['discount-element-original-price']]: true,
            [styles['discount-element-original-price-strikethrough']]:
              price && discountedPrice && !priceAndDiscountedPriceAreEqual,
          })}
        >
          {price && !priceAndDiscountedPriceAreEqual ? price : discountedPrice}
        </div>

        {price && !priceAndDiscountedPriceAreEqual && (
          <div className={styles['discount-element-discounted-price']}>
            {discountedPrice}
          </div>
        )}
      </div>
    );
  };

  const getPillowHeightLabel = (attribute) =>
    `${attribute.label} - ${PILLOW_HEIGHT_MAP[attribute.key]}`;

  const hidePrices = (attributeName) =>
    isBundleSizeMatching ||
    attributeName === VARIANT_ATTRIBUTE_NAME_PILLOW_HEIGHT ||
    attributeName === DEPTH_ATTRIBUTE_NAME ||
    attributeName === VARIANT_ATTRIBUTE_NAME_FOUNDATION_HEIGHT;

  // Variants added through "Additional Variants" can lack some attributes
  // (e.g. Harmony Anywhere has no pillow-height), so hide those selectors
  // while such a variant is selected.
  const currentVariant = selectedAttributeValues
    ? findVariantByAttributeValues(selectedAttributeValues)
    : undefined;
  const isAttributeMissingInCurrentVariant = (attributeName) =>
    !!currentVariant &&
    !currentVariant.attributes?.some(({ name }) => name === attributeName);

  return attributeNames?.length ? (
    <div className={styles.container}>
      {orderAttributeNames(attributeNames)?.map((attributeName) => {
        const uniqueKey = `VariantSelection_${attributeName}_${randomId}`;
        // need to do nothing if attribute is size or pillow size
        if (
          isSecondSku &&
          VARIANT_ATTRIBUTE_NAME_SIZES.includes(attributeName)
        ) {
          return null;
        }

        if (isAttributeMissingInCurrentVariant(attributeName)) {
          return null;
        }

        if (isColorAttribute({ attributeName })) {
          const selectedColor = options[attributeName]?.value?.find(
            (attributeValue) =>
              attributeValue.key ===
              selectedAttributeValues?.[attributeName]?.key,
          );
          const colorOptions = options?.[attributeName]?.value?.map(
            (value) => ({
              ...value,
              colorHex: options?.[
                VARIANT_ATTRIBUTE_NAME_COLOR_CODE
              ]?.value?.find((code) => code.key === value.key)?.label,
            }),
          );
          const colorOptionsWithValidVariantWithoutSort = colorOptions
            .filter((currentColor) =>
              findVariantByAttributeValues({
                ...selectedAttributeValues,
                [attributeName]: { key: currentColor.key },
              }),
            )
            .map((currentColor) => {
              const foundVariant = findVariantByAttributeValues({
                ...selectedAttributeValues,
                [attributeName]: { key: currentColor.key },
              });
              return {
                ...currentColor,
                isOutOfStock: foundVariant?.isOutOfStock,
                subheadTextForColor: foundVariant?.subheadTextForColor,
              };
            });

          const colorOptionsWithValidVariant = sortColors
            ? colorOptionsWithValidVariantWithoutSort.sort((color) => {
              if (
                initiallySelectedAttributeValues &&
                color.key === initiallySelectedAttributeValues?.color?.key
              ) {
                return -1;
              }
              if (color.isOutOfStock) {
                return 1;
              }
              return 0;
            })
            : colorOptionsWithValidVariantWithoutSort;

          return (
            <div
              className={styles['attribute-container-color']}
              key={uniqueKey}
            >
              <div className={styles['attribute-container-color-title']}>
                <div
                  className={styles['attribute-container-color-title-label']}
                >
                  {getAttributeTitleWithPrefix({
                    title: formatMessage(messages.color),
                    prefix: attributeTitlePrefix,
                  })}
                </div>
                <div
                  className={styles['attribute-container-color-title-value']}
                >
                  {selectedColor?.label}
                </div>
              </div>
              <ColorSelect
                key={attributeName}
                colors={colorOptionsWithValidVariant}
                onChange={(item) => {
                  setNewSelectedAttributeValuesStateHandler({
                    ...selectedAttributeValues,
                    [attributeName]: item,
                  });
                }}
                selectedColor={{
                  key: selectedColor?.key ?? '',
                  label: selectedColor?.label ?? '',
                }}
                showColorLabel={false}
                hasDisclaimer={colorDisclaimer}
                size={size}
              />
            </div>
          );
        }
        if (
          !VARIANT_ATTRIBUTES_TO_FILTER.includes(attributeName) &&
          !isNonPrimaryBundleSelection
        ) {
          if (
            attributeName === DEPTH_ATTRIBUTE_NAME &&
            isSingleDepthAttribute
          ) {
            return null;
          }
          return (
            <div className={styles['attribute-container']} key={uniqueKey}>
              {attributesUpperTitle &&
                attributeName !== VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE && (
                  <div className={styles['attribute-upper-title']}>
                    {attributesUpperTitle}
                  </div>
                )}
              <div
                className={classnames(styles['attribute-title-container'], {
                  [styles['bottom-margin']]: attributeName === 'size',
                })}
              >
                <div className={styles['attribute-title-content']}>
                  <div
                    className={classnames(styles['attribute-title'], {
                      [styles['no-margin']]: attributeName === 'size',
                    })}
                  >
                    {options?.[attributeName]?.label?.[locale]
                      .split('-')
                      .join(' ')}
                  </div>
                  {renderAttributeExtraInfo({
                    attributeName: options?.[attributeName]?.label?.[locale],
                  })}
                </div>
                {productVariantName !== undefined &&
                  attributeName === FEEL_ATTRIBUTE_NAME &&
                  isAttributeDescription && (
                    <span className={styles['attribute-description-text']}>
                      {formatMessage(messages[productVariantName])}
                    </span>
                  )}
                {discountSavings &&
                  attributeName !== FEEL_ATTRIBUTE_NAME &&
                  !isBundlePdp &&
                  !isBundleSizeMatching && (
                    <span className={styles['attribute-savings-banner']}>
                      {discountAmountType === 'percent_off'
                        ? formatMessage(messages.variantSavingsPercentOff, {
                            percent: getDiscountedPercentage(),
                          })
                        : formatMessage(messages.variantSavings, {
                            price: discountSavings,
                          })}
                    </span>
                  )}
              </div>

              {!!selectBoxAttributes?.length && (
                <>
                  {(!isBundlePdp &&
                    !isBundleSizeMatching &&
                    selectBoxAttributes.includes(attributeName)) ||
                  (isBundlePdp &&
                    BUNDLE_VARIANT_ATTRIBUTE_NAME_SIZES.includes(
                      attributeName,
                    )) ? (
                    <SizeSelection
                      sizes={(options?.[attributeName]?.value ?? [])
                        .map((attributeValue) => {
                          const foundVariant = findVariantByAttributeValues({
                            ...selectedAttributeValues,
                            [attributeName]: { key: attributeValue.key },
                          });
                          const isOutOfStock = !!foundVariant?.isOutOfStock;

                          return foundVariant
                            ? {
                                key: attributeValue.key,
                                label: attributeValue.label,
                                ...getPriceText({
                                  variant: foundVariant,
                                  attributeName,
                                }),
                                isOutOfStock,
                              }
                            : null;
                        })
                        .filter((option) => option)}
                      selectedKey={
                        selectedAttributeValues?.[attributeName]?.key
                      }
                      onSelect={(selectedKey) => {
                        setNewSelectedAttributeValuesStateHandler({
                          ...selectedAttributeValues,
                          [attributeName]: { key: selectedKey },
                        });
                      }}
                      hidePrices={hidePrices(attributeName)}
                      attributeName={attributeName}
                    />
                  ) : (
                    <div
                      ref={attributeName === 'size' ? sizeRef : undefined}
                      className={classnames({
                        [styles['attribute-values-container']]: true,
                        [styles['attribute-values-container-1item']]:
                          isDropdown ||
                          options?.[attributeName]?.value?.length === 1,
                        [styles['attribute-values-container-2items']]:
                          !isDropdown &&
                          options?.[attributeName]?.value?.length === 2,
                        [styles['attribute-values-container-3items-or-more']]:
                          !isDropdown &&
                          options?.[attributeName]?.value?.length >= 3,
                        [styles['attribute-values-container-show-overflow']]:
                          isDropdown,
                      })}
                    >
                      {attributeName === PILLOW_HEIGHT_NAME && isDropdown ? (
                        <DropdownSelection
                          options={options?.[attributeName]?.value?.map(
                            (attributeValue) => ({
                              value: attributeValue.key,
                              label: getPillowHeightLabel(attributeValue),
                            }),
                          )}
                          value={selectedAttributeValues?.[attributeName]?.key}
                          onChange={(value) =>
                            setNewSelectedAttributeValuesStateHandler({
                              ...selectedAttributeValues,
                              [attributeName]: { key: value },
                            })
                          }
                        />
                      ) : (
                        (!isBundlePdp ||
                          (attributeName !==
                            VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE &&
                            !hidePillowSize)) &&
                        options?.[attributeName]?.value?.map(
                          (attributeValue) => {
                            const foundVariant = findVariantByAttributeValues({
                              ...selectedAttributeValues,
                              [attributeName]: { key: attributeValue.key },
                            });
                            const isOutOfStockVariant =
                              !!foundVariant?.isOutOfStock;

                            if (foundVariant) {
                              return (
                                <AttributePicker
                                  key={attributeValue.key}
                                  valueKey={attributeValue.key}
                                  topLabel={attributeValue.label}
                                  selected={
                                    attributeValue.key ===
                                    selectedAttributeValues?.[attributeName]
                                      ?.key
                                  }
                                  onSelect={(selectedKey) => {
                                    setNewSelectedAttributeValuesStateHandler({
                                      ...selectedAttributeValues,
                                      [attributeName]: { key: selectedKey },
                                    });
                                  }}
                                  mediumLabel={
                                    isShowingPriceDetails &&
                                    !isShowingDiscountedPrices
                                      ? getPriceText({
                                          variant: foundVariant,
                                          attributeName,
                                        })?.priceAsText
                                      : undefined
                                  }
                                  mediumElement={
                                    isShowingPriceDetails &&
                                    isShowingDiscountedPrices &&
                                    !bundlePdp
                                      ? renderDiscountElement({
                                          variant: foundVariant,
                                          attributeName,
                                        })
                                      : undefined
                                  }
                                  isOutOfStock={isOutOfStockVariant}
                                />
                              );
                            }
                            return null;
                          },
                        )
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  ) : null;
};

VariantSelection.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.object),
  defaultSelectedSKU: PropTypes.string,
  onSelectVariant: PropTypes.func.isRequired,
  isShowingPriceDetails: PropTypes.bool,
  isShowingDiscountedPrices: PropTypes.bool,
  attributesExtraInfo: PropTypes.arrayOf(
    PropTypes.shape({
      attributeName: PropTypes.string.isRequired,
      isToolTip: PropTypes.bool.isRequired,
      text: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ),
  attributesUpperTitle: PropTypes.string,
  productKey: PropTypes.string,
  productName: PropTypes.string,
  discountSavings: PropTypes.string,
  attributeTitlePrefix: PropTypes.string,
  sortColors: PropTypes.bool,
  isBundlePdp: PropTypes.bool,
  isBundleSizeMatching: PropTypes.bool,
  isNonPrimaryBundleSelection: PropTypes.bool,
  size: PropTypes.string,
  hidePillowSize: PropTypes.bool,
  isDropdown: PropTypes.bool,
  isSecondSku: PropTypes.bool,
};

export default VariantSelection;
